import { useTheme } from "@/src/styles/theme-provider";
import {
  CloseCircle,
  Danger,
  InfoCircle,
  TickCircle,
} from "iconsax-react-nativejs";
import React from "react";
import { useTranslation } from "react-i18next";
import { Pressable, Text, View } from "react-native";

export type ScToastProps = Readonly<{
  text1: string;
  /**
   * Supporting detail under the headline — a list of the addresses that failed,
   * say. Passed straight through, NOT run through `t`: it carries data, not a
   * translation key.
   */
  text2?: string;
  /**
   * `info` exists because four call sites already ask for it (partial invite
   * results, the waitlist notice, the life-update warning). Until it was
   * registered in `toast.config`, every one of those rendered NOTHING at all —
   * react-native-toast-message silently drops a type its config does not define.
   */
  type: "success" | "error" | "info";
  onClose: () => void;
}>;

const ScToast = ({ text1, text2, type, onClose }: ScToastProps) => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();
  const isSuccess = type === "success";
  const isError = type === "error";

  const textColor = isSuccess
    ? "text-green-light"
    : isError
      ? "text-crimson"
      : "text-onyx";

  return (
    <View
      style={varStyle}
      className="mx-4 mt-4 rounded-[16px] bg-ash px-6 py-4 flex-row items-center gap-3 shadow-md border border-ash-3"
    >
      {isSuccess && (
        <TickCircle
          size={24}
          color={themeVars["--color-primary-default"]}
          variant="Bold"
        />
      )}
      {isError && (
        <Danger
          size={24}
          color={themeVars["--color-crimson-default"]}
          variant="Bold"
        />
      )}
      {!isSuccess && !isError && (
        <InfoCircle
          size={24}
          color={themeVars["--color-primary-default"]}
          variant="Bold"
        />
      )}

      <View className="flex-1 gap-0.5">
        <Text className={`font-medium ${textColor}`}>{t(text1)}</Text>
        {text2 ? (
          <Text className="text-xs text-slate" numberOfLines={3}>
            {text2}
          </Text>
        ) : null}
      </View>

      <Pressable
        onPress={onClose}
        hitSlop={10}
        className="p-1.5 rounded-full hover:bg-ash-3"
      >
        <CloseCircle size={20} color={themeVars["--color-slate-default"]} />
      </Pressable>
    </View>
  );
};

export default ScToast;
